import {
    Callout,
    DialogBody,
    FormGroup,
    HTMLSelect,
    Intent,
    Tag
} from "@blueprintjs/core";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { useGetScopesWithQueueProjectsQuery } from "generated/graphql";
import React, { useEffect, useMemo } from "react";
import {
    GeneratorType,
    GeneratorTypeIcon,
    getLabelForRegionAndGeneratorType
} from "types/generatorType";
import { getRedirectUrl, getRegionToSpaceDict } from "types/gitBook";
import { Loading } from "types/loadingType";
import {
    getBaseScopesForFuelTypes,
    isLocalStudyBaseEligible,
    Scope,
    ScopeName
} from "types/scope";
import { getAllCapacityCostsScopeViewsForRegion } from "types/scopeViewType";
import {
    QueueProjectsByFuelType,
    validateQueueProjectsByFuelType
} from "utils/queueProjectUtils";
import "./SelectBasePanel.scss";

interface SelectBasePanelProps {
    readonly selectedFuelTypes: ReadonlyArray<GeneratorType>;
    readonly selectedBaseScope: Scope | undefined;
    readonly onBaseScopeChange: (scopeName: ScopeName) => void;
    readonly queueProjectsByFuelType:
        | Loading<QueueProjectsByFuelType>
        | undefined;
}

const SelectBasePanel: React.FC<SelectBasePanelProps> = ({
    selectedFuelTypes,
    selectedBaseScope,
    onBaseScopeChange,
    queueProjectsByFuelType
}) => {
    const { bus, allScopesForBus, scopeToFuelTypesForBus } =
        useBusPageConfiguration();
    const user = useUserDataContext();

    const methodologySpace = getRegionToSpaceDict()[bus.region];
    const methodologyLink =
        methodologySpace !== undefined
            ? getRedirectUrl(methodologySpace, null, user)
            : undefined;

    const validationResult =
        queueProjectsByFuelType !== undefined &&
        queueProjectsByFuelType !== "loading"
            ? validateQueueProjectsByFuelType(queueProjectsByFuelType)
            : undefined;

    // The single application of the base-eligibility predicate. Both the
    // queue-project query below and the rendered dropdown derive from this
    // list, so "the scopes we asked Hasura about" and "the scopes we offer"
    // are the same population by construction rather than by coincidence.
    const eligibleBaseScopes = useMemo(
        () => allScopesForBus.filter(isLocalStudyBaseEligible),
        [allScopesForBus]
    );

    const baseScopeNames = useMemo(
        () => eligibleBaseScopes.map((s) => s.scope),
        [eligibleBaseScopes]
    );

    const allScopeViews = useMemo(
        () => [...getAllCapacityCostsScopeViewsForRegion(bus.region)],
        [bus.region]
    );

    const {
        data: scopesWithProjectsData,
        loading: scopesLoading,
        error: scopesError
    } = useGetScopesWithQueueProjectsQuery({
        variables: {
            scopes: baseScopeNames,
            scope_views: allScopeViews,
            region: bus.region
        },
        skip: baseScopeNames.length === 0
    });

    const availableBaseScopes = getBaseScopesForFuelTypes(
        eligibleBaseScopes,
        scopeToFuelTypesForBus,
        scopesWithProjectsData,
        selectedFuelTypes,
        bus.region
    );

    useEffect(() => {
        if (selectedBaseScope === undefined && availableBaseScopes.length > 0) {
            onBaseScopeChange(availableBaseScopes[0].scope);
        }
    }, [availableBaseScopes, selectedBaseScope, onBaseScopeChange]);

    if (scopesError !== undefined) {
        return (
            <DialogBody className="SelectBasePanel-body">
                <Callout intent={Intent.DANGER}>
                    Failed to load scope data. Please close and try again.
                </Callout>
            </DialogBody>
        );
    }

    return (
        <DialogBody className="SelectBasePanel-body">
            <p className="SelectBasePanel-description">
                Kick off a new study run at this POI. Nira&apos;s full{" "}
                {methodologyLink !== undefined ? (
                    <a
                        href={methodologyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {bus.region} methodology
                    </a>
                ) : (
                    `${bus.region} methodology`
                )}{" "}
                will be rerun based on your new assumptions to generate new
                injection capacity results at this bus.
            </p>
            <FormGroup
                label="You will be studied for the following fuel types:"
                className="SelectBasePanel-fuel-types-group"
            >
                <div className="SelectBasePanel-fuel-types">
                    {selectedFuelTypes.map((type: GeneratorType) => (
                        <Tag
                            key={type}
                            round
                            size="large"
                            intent="primary"
                            icon={<GeneratorTypeIcon type={type} size={16} />}
                        >
                            {getLabelForRegionAndGeneratorType(
                                bus.region,
                                type
                            )}
                        </Tag>
                    ))}
                </div>
            </FormGroup>
            <FormGroup label="Base Scope">
                <HTMLSelect
                    className="SelectBasePanel-base-scope-select"
                    value={selectedBaseScope?.scope ?? ""}
                    onChange={(e) => onBaseScopeChange(e.target.value)}
                    disabled={scopesLoading}
                >
                    <option value="" disabled>
                        {scopesLoading
                            ? "Loading scopes..."
                            : "Select a base scope"}
                    </option>
                    {availableBaseScopes.map((scope) => (
                        <option key={scope.scope} value={scope.scope}>
                            {scope.display_name}
                        </option>
                    ))}
                </HTMLSelect>
            </FormGroup>
            {validationResult !== undefined && !validationResult.valid && (
                <Callout intent={Intent.WARNING}>
                    {validationResult.message}
                </Callout>
            )}
        </DialogBody>
    );
};

export default SelectBasePanel;
